import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { Col, Row } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { Button } from '../../../ButtonV2/ButtonV2';
import { colorsV2 } from '../../../colors-v2';
import { LayoutBox as Box } from '../../../Common/Box';
import { Typography } from '../../Typography';

const DEFAULT_HEADER_PADDING = '24px 36px 16px';
const DEFAULT_BODY_PADDING = '8px 24px 16px';
const DEFAULT_FOOTER_PADDING = '16px 36px';

const StyledCol = styled(Col)`
  &:not(:last-child) {
    margin-right: 16px;
  }
  display: flex;
  align-items: center;
`;

const CloseButton = styled.div<{ headerPadding?: string }>`
  position: absolute;
  right: 0;
  margin-left: 8px;
  width: 48px;
  height: 48px;
  padding: ${(p) => p.headerPadding || DEFAULT_HEADER_PADDING};
  padding-left: 0;
  padding-bottom: 0;
  cursor: pointer;
  display: grid;
  place-items: center;

  .close-wrapper {
    position: absolute;
    z-index: 1;
    background: ${colorsV2.white100};

    .close-icon {
      font-size: 24px;
      color: ${colorsV2.gray40};
    }
  }
`;

const ModalFooter = styled(Box.Footer)`
  &:not(.with-divider) {
    box-shadow: none;
  }
`;

const ModalBody = styled(Box.Content)`
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
  }
`;

const ModalHeader = styled(Box.Header)`
  &:not(.with-divider) {
    box-shadow: none;
  }
`;

const MainModalContent = styled(Box.Container)`
  margin-top: 200px;
  min-width: 480px;
  pointer-events: auto;
  position: relative;
  @media ${deviceBreakpoints.mobile} {
    min-width: auto;
  }
`;

export interface BaseModalContentProps extends ModalProps {
  header?: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  okButtonDisabled?: boolean;
  onOk?: (e?: React.MouseEvent<HTMLElement>) => void;
  withSectionDivider?: boolean;
  headerPadding?: string;
  bodyPadding?: string;
  footerPadding?: string;
  withFooter?: boolean;
}

export const BaseModalContent = (props: BaseModalContentProps) => {
  const {
    children,
    header,
    footer,
    title,
    onOk = () => {},
    onCancel = () => {},
    okText,
    cancelText,
    icon,
    closeIcon,
    okButtonDisabled = false,
    withSectionDivider = false,
    headerPadding = DEFAULT_HEADER_PADDING,
    bodyPadding = DEFAULT_BODY_PADDING,
    footerPadding = DEFAULT_FOOTER_PADDING,
    closable = true,
    withFooter = true,
  } = props;

  return (
    <MainModalContent className="modal-content">
      {closable && (
        <CloseButton onClick={onCancel} headerPadding={headerPadding}>
          <div className="close-wrapper">
            {closeIcon || <IconOtherClose className="close-icon" />}
          </div>
        </CloseButton>
      )}
      {(header || title) && (
        <ModalHeader
          padding={headerPadding}
          className={cl('modal-header', { 'with-divider': withSectionDivider })}
        >
          {header || (
            <Row wrap={false}>
              {icon && <StyledCol>{icon}</StyledCol>}
              <StyledCol flex={1}>
                <Typography.Text variant="semi-bold/20">{title}</Typography.Text>
              </StyledCol>
            </Row>
          )}
        </ModalHeader>
      )}
      {children && (
        <ModalBody className="modal-body" padding={title || header ? bodyPadding : headerPadding}>
          <Row wrap={false}>
            {
              // Show content icon when no title
              icon && !title && <StyledCol>{icon}</StyledCol>
            }
            <StyledCol flex={1}>{children}</StyledCol>
          </Row>
        </ModalBody>
      )}
      {withFooter && (
        <ModalFooter
          padding={footerPadding}
          className={cl('modal-footer', { 'with-divider': withSectionDivider })}
        >
          {footer || (
            <Row justify="end" gutter={16}>
              {onCancel && (
                <Col>
                  <Button.Secondary onClick={onCancel}>{cancelText || 'Cancel'}</Button.Secondary>
                </Col>
              )}

              <Col>
                <Button.Primary onClick={onOk} disabled={okButtonDisabled}>
                  {okText || 'OK'}
                </Button.Primary>
              </Col>
            </Row>
          )}
        </ModalFooter>
      )}
    </MainModalContent>
  );
};
